import { isDirty } from 'redux-form';
import { createSelector } from 'reselect';

import { LoadMixer } from '../../application/loadMixers/loadMixers.types';
import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { getSelectedFeedingId, getSelectedFeedingPeriodId, getSelectedGroup } from '../../selected/selected.selector';
import { Group } from '../group/group.types';
import { getForms, getSettings } from '../settings/settings.selector';
import { Feeding, FeedingBaseRationGroup, FeedingPeriod } from './feeding.types';
import { hasMixerWithFeedingExternalSystem } from '../scaleType/scaleType.selector';

export const getFeedingPeriodList = (state: RootState): FeedingPeriod[] => state.entities.feeding.feedingPeriods;

export const getFeedingPeriodListSortedByNo = createSelector(getFeedingPeriodList, (feedingPeriods) =>
  [...feedingPeriods].sort((period1, period2) => period1.feedingNo - period2.feedingNo),
);

export const getFilteredFeedingPeriodList = createSelector(
  [getFeedingPeriodList, getSelectedGroup],
  (feedingPeriodList: FeedingPeriod[], selectedGroup: Group) => {
    const groupFeedings = (selectedGroup.feedings || []).reduce(
      (acc: string[], curr: Feeding) => acc.concat(curr.feedingPeriodId),
      [],
    );
    return feedingPeriodList.filter((feedingPeriod) => !groupFeedings.includes(feedingPeriod.id));
  },
);

export const getFeedingPeriodById = createSelector(
  [getFeedingPeriodList, (state: RootState, id: string): string => id],
  (feedingList: FeedingPeriod[], id: string) => feedingList.find((feeding) => feeding.id === id),
);

export const getSelectedFeedingPeriod = createSelector(
  [getFeedingPeriodList, getSelectedFeedingPeriodId],
  (feedingPeriodList: FeedingPeriod[], selectedFeedingPeriodId: string) => {
    return feedingPeriodList.find((feedingPeriod) => feedingPeriod.id === selectedFeedingPeriodId);
  },
);

export const getSelectedFeeding = createSelector(
  [getFeedingPeriodList, getSelectedFeedingPeriodId, getSelectedFeedingId],
  (feedingPeriodList, selectedFeedingPeriodId, selectedFeedingId) => {
    const feedingPeriod = feedingPeriodList.find((fp) => fp.id === selectedFeedingPeriodId);
    return ((feedingPeriod || { feedings: [] }).feedings || []).find((feeding) => feeding.id === selectedFeedingId);
  },
);

export const getSelectedFeedingByGroup = createSelector(
  [getSelectedGroup, getSelectedFeedingPeriodId, getSelectedFeedingId],
  (selectedGroup, selectedFeedingPeriodId, selectedFeedingId) => {
    return ((selectedGroup as Group).feedings || []).find((feeding) => feeding.id === selectedFeedingId);
  },
);

export const getFeedingPeriodsWithRecipe = createSelector(getFeedingPeriodList, (feedingPeriodList) =>
  feedingPeriodList.filter(
    (feedingPeriod) => feedingPeriod.numberOfRations >= 1 || feedingPeriod.numberOfPremixes >= 1,
  ),
);

export const isJustScheduled = createSelector(
  [getFeedingPeriodsWithRecipe, (state: RootState, premixId: string) => premixId],
  (feedingPeriodList, premixId): boolean =>
    !!feedingPeriodList
      .map((feedingPeriod) => (feedingPeriod.feedings || []).filter((feeding) => feeding.rationId === premixId))
      .filter((feedingPeriod) => feedingPeriod.length).length,
);

export const getScheduledFeedingPremix = createSelector(
  [getFeedingPeriodsWithRecipe, (state: RootState, premixId: string) => premixId],
  (feedingPeriodList, premixId): Feeding | undefined =>
    feedingPeriodList
      .map((feedingPeriod) => (feedingPeriod.feedings || []).find((feeding) => feeding.rationId === premixId))
      .reduce((acc, curr) => Object.assign(acc, curr), {} as Feeding),
);

export const hasMultipleFeedingPeriods = createSelector(
  getFeedingPeriodsWithRecipe,
  (filteredList) => filteredList.length > 1,
);

export const getBaseRationGroups = (state: RootState): FeedingBaseRationGroup[] =>
  state.entities.feeding.feedingPeriods.reduce((acc, curr) => [...acc, ...curr.baseRationGroups], []);

export const getFeedingPeriodBaseRationGroups = createSelector(
  [getFeedingPeriodById, (state: RootState, feedingPeriodId: string): string => feedingPeriodId],
  (feedingPeriod: FeedingPeriod) => feedingPeriod.baseRationGroups,
);

export const getUpdatedFeedingPeriodsForms = createSelector([getForms, (state: RootState) => state], (forms, state) => {
  const formNames = Object.keys(forms);
  const filteredForms = formNames.filter((form) => form.search(Forms.feedingPeriod) !== -1);
  return filteredForms.filter((form) => isDirty(form)(state));
});

export const getFeedingPeriodsNoByIds = (state: RootState, ids: string[]): number[] => {
  return ids
    .reduce<number[]>((acc, curr) => {
      const feedingPeriod = getFeedingPeriodById(state, curr);
      if (feedingPeriod && !acc.includes(feedingPeriod.feedingNo)) {
        return [...acc, feedingPeriod.feedingNo];
      } else {
        return acc;
      }
    }, [])
    .sort();
};

export const getFeedingPeriodByNo = createSelector(
  [getFeedingPeriodList, (_state: RootState, feedingNo: number) => feedingNo],
  (periods, feedingNo) => periods.find((x) => x.feedingNo === feedingNo),
);

export const getBaseRationGroupByLoad = createSelector(
  [getBaseRationGroups, (_state: RootState, load: LoadMixer) => load],
  (baseRationGroups, load) => {
    const loadGroups = load.groups.map((group) => group.groupId);
    const baseGroups = baseRationGroups.filter((item) => {
      if (item.feedingNo !== load.feedingNo) return false;
      const groupIds = [item.feeding.groupId, ...item.feedingsPlus.map((feeding) => feeding.groupId)];
      return loadGroups.some((id) => groupIds.includes(id));
    });
    let diff = Infinity;
    let minIndex = Infinity;
    baseGroups.forEach((group, i) => {
      group.feedingsPlus.forEach((feeding) => {
        if (Math.abs(feeding.itemIndex! - feeding.baseIndex!) < diff) {
          minIndex = i;
          diff = Math.abs(feeding.itemIndex! - feeding.baseIndex!);
        }
      });
    });
    const group = baseGroups[minIndex];
    return group;
  },
);

export const getFeedingById = createSelector(
  [getFeedingPeriodList, (_state: RootState, id: string) => id],
  (periods, id) => {
    const feedings = periods.reduce((acc, curr) => {
      return [...acc, ...(curr.feedings || [])];
    }, []);
    return feedings.find((x) => x.id === id);
  },
);

export const hasAddedFeedingPeriod = createSelector(getFeedingPeriodList, (feedingPeriodList) =>
  feedingPeriodList.some((period) => period.feedingNo > 9),
);

export const canDeleteFeedingPeriod = createSelector(
  [getFeedingPeriodList, getSettings, hasMixerWithFeedingExternalSystem],
  (feedingPeriodList, settings, hasMixer) => {
    return feedingPeriodList.length > 1 && hasMixer && settings.unlimitedFeedings;
  },
);
